/* ============================================================
   hoh-contract-content.js — THE CONTRACT display content
   Names, effect lines, narration templates, and colors for every
   id the engine emits. Pure data + tiny formatters, no DOM.
   The engine never reads this file; the UI and the (future)
   /battle-text worker narration both do. Copy obeys the Avoider.
   ============================================================ */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.HOHContractContent = factory();
}(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var RARITY_NAMES = ['COMMON', 'UNCOMMON', 'RARE', 'EPIC', 'LEGENDARY', 'MYTHIC'];
  /* rarity colors follow the Hall's existing six-tier frame system */
  var RARITY_COLORS = ['#8a8090', '#6bcb77', '#5b8ec4', '#b46bff', '#e8c674', '#ef94ff'];
  var STAT_COLORS = { M: '#e08a5a', C: '#7ec4cf', G: '#b46bff' };

  var SKILLS = {
    steady:     { n: 'STEADY HAND',     d: '+1 damage, always.' },
    opport:     { n: 'OPPORTUNIST',     d: 'On a 6: +4 damage.' },
    bigswing:   { n: 'BIG SWINGS',      d: 'Even damage rolls deal +2 damage.' },
    hunter:     { n: "HUNTER'S EYE",    d: 'Glancing blows, which normally deal half, deal full damage.' },
    firstblood: { n: 'FIRST BLOOD',     d: 'Your first attack deals +6 damage.' },
    momentum:   { n: 'MOMENTUM',        d: 'Every overflowing die: +2 damage for the rest of the fight.' },
    execute:    { n: 'EXECUTE',         d: 'While the enemy is under 30 health: your hits deal +5 damage.' },
    bulkblade:  { n: 'BULWARK BLADE',   d: 'While your side holds 20+ armor: your hits deal +4 damage.' },
    glassedge:  { n: 'GLASS EDGE',      d: 'While your side holds 30+ Ward: your damage rolls are doubled.' },
    doubletap:  { n: 'DOUBLE TAP',      d: 'First damage roll of 10+: strike again for half.' },
    shatter:    { n: 'SHATTER',         d: 'Your hits ignore armor and Ward.' },
    rampage:    { n: 'RAMPAGE',         d: 'First max roll: the whole attack repeats.' },
    mark:       { n: "ASSASSIN'S MARK", d: 'Your first attack deals double.' },
    erasure:    { n: 'ERASURE',         d: 'First 20 or 1 on your attack roll: one enemy is erased from the fight.' },
    godslayer:  { n: 'GODSLAYER',       d: 'Your crits strike x1.75 · the heaviest edge in the game.' },
    stunner:    { n: 'STUNNING BLOW',   d: "Roll one of your die's two highest numbers: the enemy loses their next turn. Once per fight." },
    summoner:   { n: 'THE HORN',        d: 'On your first highest roll: a hero joins your side and immediately attacks for a bonus d4.' },
    towergrip:  { n: 'TOWER GRIP',      d: 'Your holds gain +3 armor.' },
    vanguard:   { n: 'VANGUARD',        d: 'Your side starts the fight with +10 health.' },
    spikes:     { n: 'SPIKED PLATE',    d: 'Whoever strikes your side takes 2 back.' },
    phalanx:    { n: 'PHALANX',         d: 'Your holds also grant 3 Ward.' },
    colossus:   { n: 'COLOSSUS',        d: 'Your holds also heal 6.' },
    storedwrath:{ n: 'STORED WRATH',    d: 'While your side has armor, your attacks are stored. When the armor breaks, it all lands at once.' },
    immortal:   { n: 'THE IMMORTAL',    d: 'Once per fight: one attack on your side is blocked completely.' },
    mender:     { n: 'MENDER',          d: '+1 to all your healing.' },
    stonepalm:  { n: 'STONE PALMS',     d: 'Roll under 5: also gain 10 armor.' },
    armor:      { n: 'ARMORER',         d: 'Casts armor: blocks physical damage.' },
    ward:       { n: 'WARDKEEPER',      d: 'Casts a Ward: blocks magic damage.' },
    lifebloom:  { n: 'LIFEBLOOM',       d: 'Your heals also grant 2 armor.' },
    barrier:    { n: 'BARRICADER',      d: 'Casts a barrier: blocks the next attack whole.' },
    sanctify:   { n: 'SANCTIFY',        d: 'Your heals restore 50% more.' },
    dawnwall:   { n: 'BULWARK OF DAWN', d: 'First roll of 10+: a fleeting shield blocks the next 2 attacks.' },
    miracle:    { n: 'MIRACLE',         d: 'First time you fall under 25: heal 20.' },
    guardian:   { n: 'GUARDIAN ANGEL',  d: 'Once: the killing blow is blocked, and you heal 10.' },
    resur:      { n: 'RESURRECTION',    d: 'Your first fall is refused: rise at 1 with 50 armor and 50 Ward.' },
    warmup:     { n: 'WARM-UP',         d: 'After your song, your next attacker deals +2 damage.' },
    tempo:      { n: 'TEMPO',           d: 'On an even roll, your next attacker deals +4 damage.' },
    crescendo:  { n: 'CRESCENDO',       d: 'Each song gives your attackers +1 damage for the rest of the fight. Stacks.' },
    showstop:   { n: 'SHOWSTOPPER',     d: 'On your first roll of 10+: your attackers deal +3 damage for the rest of the fight.' },
    anthem:     { n: 'ANTHEM',          d: 'Each natural max on the song die gives your whole side +2 damage for the rest of the fight.' },
    finalact:   { n: 'FINAL ACT',       d: 'Once, when you are about to lose: the enemy loses 15 health and you gain 15.' },
    /* v13 */
    hexbolt:    { n: 'HEXBOLT',         d: 'Your crits also weaken: their next attack deals -5.' },
    rampart:    { n: 'RAMPART',         d: 'Your holds gain +6 armor.' },
    triage:     { n: 'TRIAGE',          d: 'Your heals also lift any weakening laid on your side.' },
    /* v19 */
    songblade:  { n: 'SONGBLADE',       d: 'Every one of your swings carries +2 aid.' },
    keeneye:    { n: 'KEEN EYE',        d: "Your side's crit range grows twice as fast on this hero's attacks." },
    aegisbearer:{ n: 'AEGISBEARER',     d: 'At the start of every round: your side gains 6 armor.' },
    springwell: { n: 'SPRINGWELL',      d: 'Every heal this hero casts also banks +3 song for your attackers.' },
    /* v34 R393 — R396 (Jan): these nineteen fought nameless. The engine and
       the whitelist knew them, this display map never did, so the chip
       printed the raw id in lowercase and the hover card had no story. Every
       line below is read off the engine effect, in the house vocabulary. */
    deadeye1:   { n: 'DEADEYE',         d: "Roll one of your die's two highest numbers: +3 damage." },
    deadeye2:   { n: 'DEADEYE II',      d: "Roll one of your die's three highest numbers: +5 damage." },
    deadeye3:   { n: 'DEADEYE III',     d: "Roll one of your die's three highest numbers: +7 damage. Your glancing blows always land full." },
    bloodroar:  { n: 'BLOOD ROAR',      d: "Roll one of your die's two highest numbers: +5 damage." },
    pressurepoint:{ n: 'PRESSURE POINT', d: "First roll of your die's two highest numbers: their next attack deals -4." },
    poisonedge: { n: 'POISON EDGE',     d: 'Your crits also poison: their next attack deals -4.' },
    judgment:   { n: 'JUDGMENT',        d: 'Your crits shatter 6 armor and 6 Ward off their side.' },
    twinspark:  { n: 'TWIN SPARK',      d: 'First crit on an even damage roll: the blow lands half again bigger.' },
    soultap:    { n: 'SOUL TAP',        d: 'Your crits feed you: heal 4.' },
    counterspell:{ n: 'COUNTERSPELL',   d: 'Your first crit silences: their next song or hex fizzles.' },
    deployturret:{ n: 'THE TURRET',     d: "On your first highest roll: a turret deploys · a bonus d4 now, and it fires a d4 again at the round's turn." },
    hawkcall:   { n: 'HAWK CALL',       d: 'On your first highest roll: the hawk dives for 3, and their next attack deals -2.' },
    gravecall:  { n: 'GRAVE CALL',      d: "Your first crit raises a shade. Gain 6 Ward now, and it strikes a d6 at the round's turn." },
    counterhold:{ n: 'COUNTERHOLD',     d: 'Your holds also strike back for 4.' },
    banneret:   { n: 'BANNERET',        d: "Your first hold raises a squire · +6 armor now, and it strikes a d4 at the round's turn." },
    circleward: { n: 'CIRCLE OF WARDING', d: 'Your heals also ring 4 Ward around your side.' },
    packcall:   { n: 'PACK CALL',       d: "Your first heal calls the wolf. It strikes a d4 now and at the round's turn, and 3 healing comes with it." },
    discordnote:{ n: 'DISCORDANT NOTE', d: 'Your songs also undermine: their next attack deals -3.' },
    muzzle:     { n: 'MUZZLE',          d: 'Once per fight: their next TWO songs or hexes fizzle.' },
    /* v36 R397: the legendary and mythic wave for the three thin pools */
    citadel:    { n: 'THE CITADEL',     d: 'At the start of every round: your side gains 3 armor and 3 Ward.' },
    retribution:{ n: 'RETRIBUTION PLATE', d: 'While your side holds armor: attackers take 2 back on every landed blow.' },
    avatarstone:{ n: 'THE MOUNTAIN AVATAR', d: 'Your first hold turns your side to stone: +10 armor and +10 Ward.' },
    sanctuary:  { n: 'SANCTUARY',       d: 'Your first heal also raises a barrier that blocks one whole attack.' },
    lifewell:   { n: 'THE LIFEWELL',    d: 'All your heals restore +3 more.' },
    springeternal:{ n: 'THE ETERNAL SPRING', d: 'Your side heals 4 at the start of every round, from the first bell.' },
    warchant:   { n: 'WAR CHANT',       d: 'Your songs bank double aid for your attackers.' },
    encore:     { n: 'THE ENCORE',      d: 'Your first song gives your side +2 damage on every later attack.' },
    maestro:    { n: 'THE MAESTRO',     d: 'Your songs bank double aid, and every verse adds +1 damage to all later attacks.' },
    /* v35 R394 RACE SIGNATURES — one moment per battle, any role. The line
       names WHEN it fires because a once-per-fight ability the player cannot
       time is a once-per-fight ability they think is broken. */
    rallyline:      { n: 'RALLY THE LINE',    d: 'Signature, once per battle: this hero banks +6 aid and raises 3 armor for your side. Fires on their first turn.' },
    oathstone:      { n: 'OATHSTONE',         d: 'Signature, once per battle: gain 6 armor, and the next 2 weakenings laid on your side break against the oath. Fires on their first turn.' },
    starvolley:     { n: 'STARLIT VOLLEY',    d: 'Signature, once per battle: three arrows strike for a d4 each. Fires from round 2.' },
    veilofnight:    { n: 'VEIL OF NIGHT',     d: 'Signature, once per battle: lifts every weakening from your side, and their next attack deals -4. Fires from round 2.' },
    hellpact:       { n: 'HELLPACT',          d: 'Signature, once per battle: pay 3 blood now. At the round turn, hellfire lands for 10 plus a d6, Ward first. Fires on their first turn.' },
    luckcharm:      { n: 'LUCKY BREAK',       d: 'Signature, once per battle: heal 3, and the next critical hit against your side lands flat. Fires on their first turn.' },
    ancestralbreath:{ n: 'ANCESTRAL BREATH',  d: 'Signature, once per battle: breathe for a d10 plus 2 per round (up to +6), and their next attack deals -2. Fires from round 2.' },
    halosurge:      { n: 'HALO SURGE',        d: 'Signature, once per battle: heal 6 and raise a barrier that blocks one whole attack. Fires on their first turn.' },
    overclock:      { n: 'OVERCLOCK',         d: 'Signature, once per battle: gain 6 Ward, and your side cannot be stunned for the rest of the fight. Fires on their first turn.' },
    gizmoheist:     { n: 'THE GIZMO',         d: "Signature, once per battle: pockets an enemy barrier if one stands (else 3 Ward), and raises one of your own. Fires from round 2." }
  };

  /* R314: every passive said "Below 50:" without ever saying below 50 WHAT, and
     the bonuses carried no units. Each line now names the trigger (health) and
     what the number does (damage), so a player can read it once and know. */
  var PASSIVES = {
    bloodthirst:{ n: 'BLOODTHIRST',  d: "Below 50 health: your side's next attack heals you for the damage it deals." },
    adrenaline: { n: 'ADRENALINE',   d: "Below 50 health: +4 aid banked for your side's next attacks." },
    vengeance:  { n: 'VENGEANCE',    d: "Below 40 health: +6 aid banked for your side's next attacks." },
    ironresolve:{ n: 'IRON RESOLVE', d: 'Below 30 health: gain 15 armor immediately.' },
    stoneskin:  { n: 'STONESKIN',    d: 'Below 50 health: gain 10 Ward immediately.' },
    clarity:    { n: 'CLARITY',      d: 'Below 50 health: heal 10 immediately.' },
    lastword:   { n: 'LAST WORD',    d: 'Below 30 health: your next heal is doubled.' },
    /* v13 */
    phoenix:    { n: 'EMBERHEART',   d: 'Below 30 health: heal 12 immediately.' },
    warden:     { n: 'THE WARDEN',   d: 'Below 60 health: gain 8 armor and 4 Ward immediately.' },
    focus:      { n: 'DEAD CALM',    d: "Below 40 health: your side's next attack cannot miss or glance." },
    /* v19 */
    resurgence: { n: 'RESURGENCE',   d: 'Below 35 health: heal 6 now, then 3 more at every round start.' },
    grimledger: { n: 'GRIM LEDGER',  d: "Below 55 health: this hero's swings carry +1 aid per round number (up to +4)." },
    /* v34 R393 */
    painfuel:   { n: 'PAIN FUEL',      d: 'Below 60 health: +4 aid banked for your side.' },
    innerflow:  { n: 'INNER FLOW',     d: 'Below 50 health: gain 6 Ward and +2 aid.' },
    standardbearer:{ n: 'STANDARD BEARER', d: 'Below 45 health: gain 8 armor and +2 aid.' },
    shadowbank: { n: 'SHADOW BOOKKEEPING', d: "Below 40 health: +5 aid, and your side's next blow cannot miss or glance." },
    saprising:  { n: 'SAP RISING',     d: 'Below 45 health: heal 5 now, then 2 more at every round start.' },
    runicrebound:{ n: 'RUNIC REBOUND', d: 'Below 45 health: gain 10 Ward, and attackers take 1 back.' },
    apex:       { n: 'APEX INSTINCT',  d: "Below 50 health: +4 aid, and your side's next blow cannot miss or glance." },
    entropy:    { n: "ENTROPY'S FAVORITE", d: 'Below 50 health: your 1s read as 3s.' },
    saintsledger:{ n: "SAINT'S LEDGER", d: 'Below 40 health: heal 8, and every later heal restores +2.' },
    standingova:{ n: 'STANDING OVATION', d: 'Below 40 health: +6 aid, and your songs carry +1 for the rest of the fight.' },
    autorepair: { n: 'AUTO-REPAIR',    d: 'Below 50 health: gain 8 armor and heal 4.' },
    fineprint:  { n: 'THE FINE PRINT', d: 'Below 30 health: the enemy side takes 8, once.' },
    /* v36 R397: the legendary band grows */
    lastlight:  { n: 'THE LAST LIGHT',  d: "Below 30 health: heal 10, and your side's next attack cannot miss." },
    ironwill:   { n: 'IRON WILL',       d: 'Below 35 health: gain 12 armor, and every weakening on your side lifts.' },
    overgrowth: { n: 'OVERGROWTH',      d: 'Below 40 health: heal 5 now, then 4 more at every round start.' },
    /* v35 R394 RACE WAKES */
    undaunted:       { n: 'UNDAUNTED',           d: 'Below 40 health: heal 5, and your Last Stand re-arms · one more surge when the brink comes again.' },
    mountainpatience:{ n: "MOUNTAIN'S PATIENCE", d: 'Below 45 health: every weakening on your side lifts, gain 6 armor, and the next one laid breaks against the stone.' },
    feystep:         { n: 'FEY STEP',            d: 'Below 50 health: the next clean strike against your side is forced to glance. Only a true shot pierces the veil.' },
    webofshadows:    { n: 'WEB OF SHADOWS',      d: 'Below 45 health: their next attack deals -3, and their next song or hex fizzles.' },
    infernalrebate:  { n: 'INFERNAL REBATE',     d: 'Below 40 health: the next blow that lands on your side pays half its damage back as healing.' },
    braveheart:      { n: 'BRAVE HEART',         d: 'Below 50 health: heal 3, and the next critical hit against your side lands flat.' },
    dragonpride:     { n: "DRAGON'S PRIDE",      d: "Below 45 health: +3 aid, and your side's crit range grows two steps at once." },
    radiantguard:    { n: 'RADIANT GUARD',       d: 'Below 40 health: heal 4 and raise a barrier that blocks one whole attack.' },
    failsafecore:    { n: 'FAILSAFE CORE',       d: 'Below 40 health: every weakening lifts, gain 5 armor, and your side cannot be stunned again.' },
    contingencygizmo:{ n: 'CONTINGENCY GIZMO',   d: 'Below 45 health: a pocket gizmo deploys and strikes a d4 at the round turn.' }
  };

  var CONDS = {
    moon:   { n: 'BLOOD MOON',    ico: '🌕', c: '#c0564a', d: 'Critical hits strike x1.55 today.' },
    high:   { n: 'HIGH GROUND',   ico: '⛰', c: '#e8c674', d: "Each side's first attack deals +5 extra." },
    famine: { n: 'SHADOW FAMINE', ico: '🌑', c: '#b46bff', d: 'All healing is cut in half today.' },
    iron:   { n: 'IRON RAIN',     ico: '🏹', c: '#e08a5a', d: 'Every attack deals +2 extra.' },
    omen:   { n: 'OMEN WINDS',    ico: '🌬', c: '#7ec4cf', d: 'Every die is rolled twice. The better roll counts.' },
    glass:  { n: 'GLASS FIELD',   ico: '❄', c: '#7ec4cf', d: 'You fall when your health drops to 10, not 0.' },
    surge:  { n: 'ARCANE SURGE',  ico: '🔮', c: '#b46bff', d: 'Spells and healing get +4.' },
    echo:   { n: 'ECHO CHAMBER',  ico: '〰', c: '#e8c674', d: 'A die that rolls its highest number rolls once more.' },
    fog:    { n: 'FOG OF WAR',    ico: '🌫', c: '#8a8090', d: 'Attack rolls of 8 or less glance for half.' },
    /* R315: three new battlefields. Each hooks one existing rule so it is easy
       to read at a glance and easy to reason about mid-fight. */
    bulwark:{ n: 'THE BULWARK',   ico: '🛡', c: '#e08a5a', d: 'Armor and Ward do not fade this battle.' },
    tempest:{ n: 'THE TEMPEST',   ico: '🌩', c: '#8ad0e0', d: 'Every 1 rolled becomes the die\'s highest number.' },
    siphon: { n: 'BLOOD SIPHON',  ico: '🩸', c: '#c0564a', d: 'A critical hit heals the attacker for half its damage.' },
    gold:   { n: 'GOLDEN HOUR',   ico: '🌅', c: '#e8c674', d: 'Every heal gives your side\'s next attack +2 damage.' },
    quake:  { n: 'AFTERSHOCK',    ico: '⛰', c: '#e08a5a', d: 'Every critical hit shakes 5 armor and 5 Ward off BOTH sides.' },
    veil:   { n: 'THE VEIL',      ico: '🌫', c: '#8ad0e0', d: 'Every attack is 10% more likely to be dodged, both sides.' },
    /* v13: two that reward a build, two that forbid one */
    anvil:  { n: 'THE ANVIL',     ico: '⚒', c: '#e08a5a', d: 'All armor and Ward gained is half again as strong.' },
    bell:   { n: 'THE OPENING BELL', ico: '🔔', c: '#e8c674', d: 'Every attack in round 1 deals +4.' },
    thorns: { n: 'FIELD OF THORNS', ico: '🥀', c: '#c0564a', d: 'Every landed attack costs the attacker 2 health.' },
    silence:{ n: 'THE SILENCE',   ico: '🔇', c: '#8a8090', d: 'Songs and pin-downs do nothing today.' },
    /* v33: the two fields that change WHO IS STANDING rather than what a die
       does. Both are symmetric, because a field rule that helps one side is a
       difficulty setting wearing a costume. */
    wander: { n: 'THE WANDERER',  ico: '\uD83E\uDDED', c: '#8fe0a5', d: 'An extra fighter joins both sides.' },
    absent: { n: 'THE ABSENT',    ico: '\uD83D\uDD73', c: '#c98a84', d: 'One fighter on each side sits this one out.' },
    still:  { n: 'A QUIET FIELD', ico: '🕯', c: '#8a8090', d: 'No special rule today.' }
  };

  /* attack narration, class-keyed, %N% attacker, %T% target */
  var LINES = {
    Fighter:   ['%N% shield-checks %T% and swings through', '%N% steps inside %T%’s reach and cuts upward', '%N% breaks %T%’s spear and keeps coming'],
    /* R316: "shrugs the hit and swings harder" read as a reaction rather than a
       strike. Every attack line now leads with the blow being thrown. */
    Barbarian: ['%N% comes for %T% screaming, axe first', '%N% takes the hit and buries the axe in %T% anyway', '%N% throws %T% into two others'],
    Paladin:   ['%N% advances on %T% behind the oath, blade high', '%N% takes %T%’s blow and answers it'],
    Monk:      ['%N% is past %T%’s guard before it closes', '%N% turns one fist into three on %T%'],
    Rogue:     ['%N% slips the torchlight and knifes %T%’s flank', '%N% is behind %T%. Nobody saw when', '%N% cuts %T%’s strap and the shield drops'],
    Ranger:    ['%N% puts an arrow through the gap in %T%’s guard', '%N% marks %T% and lets fly'],
    Wizard:    ['%N% folds fire over %T%’s rank', '%N% speaks one word and the ground under %T% answers', '%N% pins %T% in force-light'],
    Sorcerer:  ['%N% lets the storm out at %T%', '%N% burns bright and %T%’s side breaks'],
    Warlock:   ['%N%’s patron reaches through %T%, briefly', '%N% whispers and the shadows bite %T%'],
    Artificer: ['%N%’s contraption roars at %T%', '%N% throws something at %T% that should not fit in a bag'],
    Cleric:    ['%N% calls light down on %T%', '%N% brings the symbol down on %T% like a hammer', '%N% burns %T% clean with consecrated fire'],
    Druid:     ['%N% wakes the roots under %T%’s feet', '%N% sets the briars on %T%'],
    /* R316: every line must name a physical ACTION the hero took. "laughs, and
       somehow that helps" described a mood and left the player guessing what
       actually happened, which reads as filler next to a real damage number. */
    Bard:      ['%N% strikes a chord that lands on %T% like a war drum', '%N% turns the verse on %T% and it cuts', '%N% shouts %T% off their footing']
  };
  /* R450: narration keeps its drama, the jargon words inside it go plain.
     "the line" meant the party and read as a riddle next to a real number. */
  var TANKLINES = ['%N% plants the shield and the party steadies', '%N% steps into the blow meant for the party', '%N% locks shields and calls the wall', '%N% braces, and the ground holds'];
  /* R353: HEALS STOPPED WEARING WAR PAINT. The bless classes drew their heal
     narration from their ATTACK pools (R352 let them keep "their own" lines,
     but their only lines WERE attack lines) — so a +25 heal printed "strikes
     a chord that lands like a war drum", an attack sentence wearing a green
     number. Each bless class now owns a true support pool; attack pools above
     stay strikes. Display-only. */
  var BLESSLINES = {
    Cleric: ['%N% raises the symbol and the ranks hold', '%N% steadies the wounded mid-swing', '%N% speaks the old blessing over the party'],
    Druid:  ['%N% coaxes green from the trampled ground', '%N% knits the party whole with root and moss'],
    Bard:   ['%N% turns the tune and the party breathes again', '%N% carries the chorus and the wounded stand']
  };
  var GENERIC = { attack: '%N% finds an opening in %T% and takes it', bless: '%N% rallies the party' };

  /* deterministic line pick: seeded by the beat so replays narrate identically.
     R352: BLESS ACTIONS STOPPED BORROWING ATTACK VERBS. Every class fell into
     its attack lines regardless of kind, so a Wizard HEALING printed "folds
     fire over THE's rank +54" — an attack sentence, a heal number, and the
     'the line' fallback shredded to "THE" by the first-word cut. Only classes
     whose lines read as support (Cleric, Druid, Bard) keep them for bless;
     everyone else gets the generic rally line. The fallback keeps its words. */
  function lineFor(cls, kind, name, target, beatIndex) {
    var arr = kind === 'tank' ? TANKLINES
            : kind === 'bless' ? (BLESSLINES[cls] || [GENERIC.bless])
            : (LINES[cls] || [GENERIC[kind] || GENERIC.attack]);
    var l = arr[beatIndex % arr.length];
    return l.replace(/%N%/g, (name || 'THE HERO').split(' ')[0].toUpperCase())
            .replace(/%T%/g, target ? target.split(' ')[0].toUpperCase() : 'THE PARTY');
  }

  /* v2: what each of the three class moves means, keyed by move TYPE.
     The gear panel's hover tips read these. */
  var MOVE_TIPS = {
    hold:  'Turns the roll into armor for your side.',
    atk:   'A clean strike at their health.',
    big:   'A heavy blow · hits harder, then needs a breather.',
    hybrid:'Strike and shield in one motion.',
    quick: 'A fast cut · an ally follows up immediately.',
    buff:  'Rallies the side: your next attacker hits harder.',
    debuff:'Undermines them: their next attack lands weaker.',
    dodge: 'Raises your chance to dodge the next blows.',
    heal:  'Heals your side.',
    cast:  'Raises armor or Ward over your side.'
  };

  /* v25 THE RECRUIT'S CALLINGS. Display truth for the five bonus actions a
     recruit can bring. `n` is the one-word calling for the bench chip, `v` is
     the verb the log uses when it fires, `d` is the full line for anywhere the
     rules are actually being read (the card panel, a help sheet). Nothing here
     is printed on a candidate at pick time — the class is the tell, and the
     numbers belong to the log where they are engine truth. */
  /* R450 (Jan): the callings say the JOB now. BLADE/MENDER/WARD/SPARK/SHADOW
     were invented one-worders a new player had to decode from a tooltip, which
     is the proof the label was wrong. Ids are engine truth and never change;
     only these display strings did. */
  var CALLINGS = {
    mender: { n: 'HEALER',   v: 'heals your party',          d: 'Each round, after your party acts: heals your party by their die roll.' },
    blade:  { n: 'ATTACKER', v: 'attacks',                   d: 'Each round, after your party acts: hits the enemy for their die roll. Shields absorb it first.' },
    ward:   { n: 'DEFENDER', v: 'adds armor',                d: 'Each round, after your party acts: adds their die roll to your armor.' },
    spark:  { n: 'BOOSTER',  v: 'boosts your next attacks',  d: 'Each round, after your party acts: banks twice their die roll as a bonus on your next attacks.' },
    shadow: { n: 'WEAKENER', v: 'weakens their next attack', d: 'Each round, after your party acts: the enemy\'s next attack lands weaker by their die roll.' },
    /* Engine 84: the Ward lane of the class table (Artificer, Wizard). */
    warder: { n: 'WARDER',   v: 'adds Ward',                 d: 'Each round, after your party acts: adds their die roll to your Ward.' }
  };

  var NOTE_TEXT = {
    stun: 'STUNNING BLOW · they lose their next turn',
    summon: 'THE HORN SOUNDS',
    opport: 'OPPORTUNIST +4', bigswing: 'BIG SWINGS +2', firstblood: 'FIRST BLOOD +6',
    execute: 'EXECUTE +5', bulkblade: 'BULWARK BLADE +4', glassedge: 'GLASS EDGE, damage doubled',
    doubletap: 'DOUBLE TAP', rampage: 'RAMPAGE, THE BLOW REPEATS', mark: "ASSASSIN'S MARK x2",
    hunter: "HUNTER'S EYE, it lands full", highground: 'HIGH GROUND +5', lifesteal: 'LIFESTEAL',
    spiked: 'spiked, 2 back', stonepalm: 'STONE PALMS +10 ARMOR', dawnwall: 'BULWARK OF DAWN',
    miracle: 'MIRACLE', tempo: 'TEMPO +4', crescendo: 'CRESCENDO', showstop: 'SHOWSTOPPER',
    anthem: 'ANTHEM +2', lastword: 'LAST WORD, it lands double', aftershock: 'AFTERSHOCK, the walls shake',
    /* v13 */
    bell: 'THE OPENING BELL +4', thorns: 'the thorns take 2 from the attacker',
    /* v34 R393 */
    deadeye: 'DEADEYE, the arrow finds the seam', bloodroar: 'BLOOD ROAR +5',
    pressurepoint: 'PRESSURE POINT, their next blow weakens', poisonedge: 'POISON EDGE, their next blow weakens',
    judgment: 'JUDGMENT, 6 armor and Ward shatter', twinspark: 'TWIN SPARK, the blow lands half again',
    soultap: 'SOUL TAP, 4 comes back', counterspell: 'COUNTERSPELL, their next spell fizzles',
    counterhold: 'COUNTERHOLD, the wall answers for 4', circleward: 'CIRCLE OF WARDING +4 Ward',
    discordnote: 'DISCORDANT NOTE, their next blow weakens', muzzle: 'MUZZLE, their songs fizzle',
    truestrike: 'TRUE STRIKE, it cannot miss',
    hexbolt: 'HEXBOLT · their next attack −5', triage: 'TRIAGE · the weakening is lifted',
    focus: 'DEAD CALM · the blow cannot miss',
    /* v26 */
    rival: 'THE RIVAL PRESSES',
    /* v35 R394 — the race moments, as the beat notes see them */
    sanctuary: 'SANCTUARY, a barrier rises with the heal',
    encore: 'THE ENCORE, the crescendo begins',
    warchant: 'WAR CHANT, the aid doubles',
    maestro: 'THE MAESTRO conducts, the crescendo swells',
    avatarstone: 'THE MOUNTAIN AVATAR, the side turns to stone',
    feystep: 'FEY STEP, the blow slides off the veil',
    feypierce: 'the true shot pierces the veil',
    luckybreak: 'LUCKY BREAK, the killing edge lands flat',
    stunfizzle: 'the stun breaks on the overclocked frame',
    rebate: 'INFERNAL REBATE, the pact drinks the blow'
  };

  /* ============ R453: PLAIN WORDS FOR THE RULES (Jan) ============
     "we are using weird vocab that is unintuitive like 'one threshold
     sharpened' what does that even mean? and what is '1 edge to every swing'".
     He is right, and the words were never a design choice: they are the
     ENGINE's internal lane names (edge, ward, pin, pool, sharpen, body,
     line) leaking straight onto the screen through the rules card, the
     draft sheet and the ability chips.

     The engine file itself is spliced byte-identical into the worker, so
     rewriting its strings would force a worker re-paste and a fresh parity
     run for a pure copy change. So this is a DISPLAY-LAYER rewriter, the
     same move R450's de-saga pass used for engine-stamped names: the rule
     text is translated on its way to the screen and the engine never
     changes. `plain()` is the one door; every surface that prints an
     engine-authored description runs through it.

     LAW: names stay, descriptions go plain (copy-voice.md, the costume-word
     ban: "card and skill NAMES stay, the description goes plain"). So
     THE WARDING keeps its name and its line reads "Ward".

     The mechanical truths behind the translations, read off the engine so
     the plain words are TRUE and not just friendlier:
       edge      flat bonus damage added to a swing (LAWS caps at 4/side)
       sharpen   raises the crit range: crits land on 19+, then 18+
       ward      the Ward lane (armor blocks steel, Ward blocks magic)
       pin       lowers the damage of the target's NEXT attack
       pool      the side's team health total
       song/bank banked bonus damage for the side's next attackers
       stir      the telegraph a passive prints before it wakes
       glance    a blow that lands for half */
  var PLAIN_PHRASES = [
    /* whole clauses first: these read wrong if translated word by word */
    /* ============ R472 · THE FOURTEEN BATTLE PLANS ============
       Jan: "a lot of them sound like they're the exact same thing and it
       doesn't help. Like your side is 4% harder to hit, but then the quick
       step is also your hero is harder to hit plus six dodge ... to a person
       that doesn't know what the game is about, that doesn't make any sense
       at all."

       He is right twice over, and the second half is worse than the copy.
       THE FLEET FOOT and QUICKSTEP are THE SAME DIAL: both are `st.dodge.A`,
       one adds 4 and the other adds 6, ten lines apart in the same function.
       The "%" in the FLEET FOOT line was never true. Dodge is a flat counter
       capped at 45, so "4%" and "+6 dodge" were two inventions describing one
       number. They read as different mechanics because the WORDS differed,
       and they were dealt against each other in the same band.

       So same dial now means same grammar and only the number changes, and a
       different dial means different grammar and a differently named unit.
       The pairs are deliberate: fleetfoot/quickstep 4 against 6,
       stoutline/ironwall 6 against 12, marchsong/openingcut 4 against 3.

       THESE LAND HERE AND NOT IN THE ENGINE, for the reason this file's own
       header gives: the engine is spliced byte-identical into the worker, so
       editing its strings would force a Cloudflare paste and a parity run for
       a pure copy change. plain() is the one door.

       Nine of these also correct a line that was WRONG, not merely dull:
       THE WHETSTONE's 19 comes back after five attacks without a crit rather
       than being spent, THE BREATHER re-arms every fight, THE IRON HIDE and
       THE LAST WARD do too, THE ROAD SONG's "4 songs banked" is simply +4 on
       your first attack, THE VETERANS CALL caps at a d12, THE TITAN GRIP is
       62 in 100 against a baseline of 50 that was never printed, and THE
       HUNTING LINE's "pins 2" is the phrase this table already translates for
       every other card in the game.

       Verified by harness/copy.cjs: each maps to exactly the line below, each
       output is idempotent, and no two plans render the same sentence. */
    /* ============ R473 · THE STAGE RULES, IN PLAIN WORDS ============
       Jan: "in plain words is not just plain words for just that champion
       ceremony. It's across the board ... stop using this bullshit fantasy
       language and make things clearer."

       These are the arena, the stage rule and the accepted offer, the three
       cards on the panel a player hovers before entering a fight. They are
       CHROME, so they get plain words. The NAMES are fiction and stay.

       Nine of these were not merely unclear, they were WRONG, and each one was
       traced to the engine line that proves it:
         WARLORDS RAMPART  only the first 3 attacks a round bank armor, and a
                           Fighter banks none at all (floor(1/2) = 0)
         GILDED MENAGERIE  the extra body is on THEIR bench. The player gets
                           nothing, and "each side" said otherwise
         THE SUNKEN GALLERY  healEaseBoth 90 makes healing 10% WEAKER, and the
                           old line read as a buff
         THE SHATTERED THRONE  the cap is armor and Ward COMBINED, and
                           "12 total shield" read as 12 of each
         THE HEX EYE       fires at the start of round 2 with no attacker
                           involved, so "their opener" named a thing that is
                           not there
       The rest traded engine vocabulary (edge, swing, ramp, brink, stir, the
       swinger, top two faces, compress toward 64) for the number a player can
       act on.

       TWO ARE DELIBERATELY MISSING. HALL OF ECHOES and THE DAMPEN both ride
       songBonus / songHalf, which computePathFx writes and NOTHING reads. They
       do nothing at all. Giving them clearer copy would ship a clearer lie, so
       they keep their vague line until somebody either builds the dial or
       deletes the rule. HALL OF ECHOES is the first arena a player meets, at
       stage 11. */
    ['Every swing banks 1 armor for the swinger', 'Both sides gain 1 armor on their first 3 attacks each round. Fighters gain none'],
    ['One extra body is allowed on each side', 'The enemy gets one extra fighter on their bench'],
    ['Heavy blows compress toward 64 instead of 71', 'Very big hits get cut down more, for both sides'],
    ['Both sides drink 5 percent of the damage they land', 'Both sides heal 5 percent of the damage they land'],
    ['Shields shed a tenth more at each round end', 'Shields keep 45 percent at the end of each round instead of 50'],
    ['The round ramp softens a tenth for both sides', 'Attacks climb 40 percent a round instead of 50, for both sides'],
    ['Healing eases a tenth and tires one round later', 'Both sides heal 10 percent less, and healing stays strong one round longer'],
    ['Passives stir 15 out instead of 10', 'Passives warn you 15 health before they wake instead of 10'],
    ['Neither side may hold more than 12 total shield', 'Armor and Ward together cannot pass 12, on either side'],
    ['One extra blade waits on their bench from the first bell', 'The enemy brings one extra fighter on their bench'],
    ['Their top two faces surge for 4 edge', 'Their two highest rolls each hit 4 harder'],
    ['Their opener stuns your strongest hero on round 2', 'At the start of round 2 they stun your strongest hero'],
    ['They drink 8 percent of the damage they land', 'They heal 8 percent of the damage they land'],
    ['Your healing tires from round 4', 'Your healing gets weaker from round 4'],
    ['No easing at the brink for your side', 'Your side loses its 12 percent damage cut at low health'],
    ['Your first item lands one turn late', 'Your first Second Wind, Arcane Volley or Call for Aid lands one turn late'],
    ['They bank 2 armor each round and your draft upgrades a tier', 'They gain 2 armor each round, and your next battle plan offers a stronger one'],
    ['They field 2 more bodies and a win pays a second draft pick', 'They field 2 more fighters, and winning lets you take two battle plans'],
    ['Healing tires from round 2 for both sides and the draft pool widens', 'Healing weakens from round 2 for both sides, and your battle plans get stronger'],
    /* R472: THE BOUNTY, the offer Jan photographed. "Your draft deals a
       fourth option" uses `draft` as a noun the game never defines
       anywhere a player can see. The screen it refers to is headed PICK A
       BATTLE PLAN, so that is the name it gets. */
    ['They swing 3 harder and your draft deals a fourth option', 'They hit 3 harder, and your next battle plan offers a fourth choice'],
    /* ============ R483 · THE LEGENDARY BAND, IN PLAIN WORDS ============
       Full-string rules, sitting with the other thirteen draft plans, so they
       win before any word-level rule can half-convert them. These are the four
       that open at stage 61 and they are the first thing a player sees when
       the band unlocks, so they carry the most weight of any plan line. */
    ['Every bench body you lose heals your party 10', 'Every time you lose a bench hero, your party heals 10'],
    ['Every time one of them falls, one of you takes an extra turn', 'Every time one of them falls, one of your heroes takes an extra turn'],
    ['Your first crit each round raises a barrier', 'Your first critical hit each round raises a barrier'],
    ['Every heal sharpens your next swing by 10', 'Every heal makes your next attack hit 10 harder'],
    ['Crit threshold starts 1 lower, every fight', 'You start every fight one step closer to a critical hit'],
    ['Open every fight with 6 armor', 'Start every fight with 6 armor'],
    ['Open every fight with 6 ward', 'Start every fight with 6 Ward'],
    ['Open every fight with 4 songs banked', 'Your first attack each fight hits 4 harder'],
    ['Your side is 4% harder to hit, every fight', '4 more attacks out of every 100 miss your party'],
    ['Your first swing each fight carries 3 edge', 'Your first attack each fight hits 3 harder'],
    ['Heal 6 at the start of round 3', 'Heal 6 team health when round 3 starts, every fight'],
    ['The first blow of 25 or more against you lands 6 lighter', 'The first hit of 25 or more each fight lands 6 lighter'],
    ['Your first swing each round pins 2', 'Your first attack each round weakens their next attack by 2'],
    ['Your bench rolls one die size up', 'Your bench rolls a bigger die, up to a d12'],
    ['From round 4 your swings carry 2 edge', 'From round 4 on, every attack you make hits 2 harder'],
    ['The first time you cross 35 heal 8', 'Once each fight, dropping under 35 team health heals you 8'],
    ['Your first miss each fight becomes a glance', 'Your first miss each fight lands for half instead'],
    ['Your shields keep a quarter more at each round turn', 'Shields fade at each round end. Yours keep 62 in 100, not 50'],
    ['your crit line starts one threshold sharpened', 'you start each fight closer to a critical hit'],
    ['crit line starts one threshold sharpened', 'starts each fight closer to a critical hit'],
    ['your crit line starts sharpened', 'you start each fight closer to a critical hit'],
    ['crit lines sharpen at', 'critical hits get easier after'],
    ['critless swings', 'attacks without one'],
    ['one threshold sharpened', 'closer to a critical hit'],
    ['crits one line lower', 'lands a critical hit more easily'],
    ['crit range grows', 'critical hits get easier'],
    ['they crit at 19', 'they land critical hits on 19 or better'],
    ['both front lines', 'both sides'],
    ['front line', 'front rank'],
    /* the costume-word ban: a party is a party, never a line. Ordered AFTER
       the crit-line rules above so 'crit line' is long gone by here. */
    ['one of their line', 'one of their party'],
    ['joins their line', 'joins their party'],
    ['their line', 'their party'],
    ['your line', 'your party'],
    /* an opening 'Bank' is just 'Gain' in every language a player speaks */
    ['bank n armor', 'gain N armor'],
    ['bank n ward', 'gain N ward'],
    ['when your pool first crosses', "when your team's health first drops past"],
    ['when the pool first crosses', "when your team's health first drops past"],
    ['the pool first crosses', "your team's health first drops past"],
    ['while their pool is above half', 'while their team health is above half'],
    ['their pool', 'their team health'],
    ['your pool', 'your team health'],
    ['at the round turn', 'at the end of the round'],
    ['the round turn', 'the end of the round'],
    ['at the brink', 'at low health'],
    ['from the first bell', 'from the start of the fight'],
    ['the first bell', 'the start of the fight'],
    ['one die size up', 'a bigger die'],
    ['a die one size up', 'a bigger die'],
    ['rolls one die size up', 'rolls a bigger die'],
    ['their top two faces', 'their two highest rolls'],
    ['top two faces', 'the two highest rolls'],
    ['becomes a glance', 'lands for half instead'],
    ['wake without a stir', 'wake with no warning'],
    ['passives stir', 'passives warn'],
    ['out instead of', 'health early instead of'],
    ['the ward eats it first', 'their Ward blocks it first'],
    ['prick the next blow that lands', 'the next blow that lands takes damage back'],
    ['bank half again as armor', 'gain half again as armor'],
    ['your songs bank at half strength', 'your song bonuses are half as strong'],
    ['songs banked', 'bonus damage banked'],
    ['bank n songs', 'gain N bonus damage for your attacks'],
    ['songs bank', 'song bonuses bank'],
    ['call n spirits', 'call N spirits'],
    ['swinging n harder', 'hitting N harder'],
    ['each swings softer', 'each one hits softer'],
    ['they swing', 'they hit'],
    ['your swings carry', 'your attacks gain'],
    ['their next by', 'their next attack by'],
    ['this heros', 'this hero\'s'],
    ['your heros', 'your hero\'s'],
    ['swings climb', 'attacks climb'],
    ['casters swing', 'casters hit'],
    ['every swing banks', 'every attack banks'],
    ['the swinger', 'the attacker'],
    /* edge, the most common leak, in every shape the catalogs use it */
    ['carries 1 edge for both sides', 'hits 1 harder, for both sides'],
    ['carry 2 edge', 'hit 2 harder'],
    ['carries 3 edge', 'hits 3 harder'],
    ['carries 1 edge', 'hits 1 harder'],
    ['carries n edge', 'hits N harder'],
    ['answer with n edge', 'answer N harder'],
    ['adds 2 edge to your next swing', 'makes your next attack hit 2 harder'],
    ['adds 3 edge to your next swing', 'makes your next attack hit 3 harder'],
    ['surge for 4 edge', 'hit 4 harder'],
    ['pin their next swing by n', 'weaken their next attack by N'],
    ['pins 2', 'weakens their next attack by 2'],
    ['mend n team health', 'heal N team health'],
    ['mend n and', 'heal N and']
  ];
  /* single words, applied after the phrases, on word boundaries only */
  var PLAIN_WORDS = [
    /* Jan, 2026-09-09: the resource is WARD on screen, everywhere. The engine
       is spliced byte-identical into the Worker, so its own strings still say
       both "ward" and the retired "spell shield"; both land here as Ward. */
    ['spell shield', 'Ward'], ['spell shields', 'Ward'],
    ['ward', 'Ward'], ['wards', 'Ward'],
    ['swing', 'attack'], ['swings', 'attacks'],
    ['mend', 'heal'], ['mends', 'heals'],
    ['bodies', 'fighters'], ['body', 'fighter'],
    ['blade', 'fighter'],
    ['eases', 'softens'], ['easing', 'softening'],
    ['shed', 'fade'], ['sheds', 'fades'],
    ['opener', 'first attacker']
  ];
  /* the safety net: any 'N edge' / '3 edge' the phrase list did not already
     reshape still has to reach the player as damage, never as a lane name */
  var PLAIN_RX = [
    [/\bN edge\b/g, 'N bonus damage'],
    [/\b(\d+) edge\b/gi, '$1 bonus damage'],
    /* a bare number followed by 'song' is always the bonus-damage bank */
    [/\b(\d+) song\b/gi, '$1 bonus damage']
  ];
  function _rxEsc(s) { return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }
  var _PH_RX = null, _WD_RX = null;
  function _buildPlain() {
    if (_PH_RX) return;
    _PH_RX = PLAIN_PHRASES.map(function (p) { return [new RegExp(_rxEsc(p[0]), 'gi'), p[1]]; });
    _WD_RX = PLAIN_WORDS.map(function (p) { return [new RegExp('\\b' + _rxEsc(p[0]) + '\\b', 'gi'), p[1]]; });
  }
  /* the one door. Give it an engine-authored description, get the same
     sentence in words a first-time player already owns. Idempotent and
     safe on any string: nothing matches, nothing changes. */
  function plain(s) {
    if (s == null) return '';
    var t = String(s);
    if (!t) return t;
    _buildPlain();
    var i;
    for (i = 0; i < _PH_RX.length; i++) t = t.replace(_PH_RX[i][0], _PH_RX[i][1]);
    for (i = 0; i < PLAIN_RX.length; i++) t = t.replace(PLAIN_RX[i][0], PLAIN_RX[i][1]);
    for (i = 0; i < _WD_RX.length; i++) t = t.replace(_WD_RX[i][0], _WD_RX[i][1]);
    /* the replacements are written lowercase so they read mid-sentence;
       a sentence that started with a capital keeps one */
    if (/^[a-z]/.test(t) && /^[A-Z]/.test(String(s))) t = t.charAt(0).toUpperCase() + t.slice(1);
    return t;
  }

  /* ============ R450b: THE ABILITY LIBRARY'S NAMES ============
     The 440-row class library (hoh-contract-engine.js ABILITIES) rolls
     onto real gear once classPools is on, and until now every one of its
     ids fell through skillName's raw-id fallback — a legendary chip
     literally reading "wl_perfectbargain". One display name per id,
     derived from the glued id words and hand-checked; descriptions come
     from the engine's own effect text (ABILITIES[id].t), read through a
     guarded hook so this file still loads anywhere the engine is absent.
     SKILLS and PASSIVES above always win — they are the curated copy. */
  var ABIL_NAMES = {
    wl_bloodsip: 'BLOOD SIP', wl_darkdraught: 'DARK DRAUGHT', wl_pettyimp: 'PETTY IMP', wl_blackcoin: 'BLACK COIN',
    wl_leechmark: 'LEECH MARK', wl_gravepair: 'GRAVE PAIR', wl_debtcollect: 'DEBT COLLECT', wl_threeknocks: 'THREE KNOCKS',
    wl_hexopen: 'HEX OPEN', wl_soulinterest: 'SOUL INTEREST', wl_redledger: 'RED LEDGER', wl_soulmortgage: 'SOUL MORTGAGE',
    wl_opengate: 'OPEN GATE', wl_compound: 'COMPOUND', wl_perfectbargain: 'PERFECT BARGAIN', wl_gravespark: 'GRAVE SPARK',
    wl_openingtithe: 'OPENING TITHE', wl_smallcurse: 'SMALL CURSE', wl_familiar: 'FAMILIAR', wl_soulsiphon: 'SOUL SIPHON',
    wl_gravewhisper: 'GRAVE WHISPER', wl_sanguine: 'SANGUINE', wl_wardingdebt: 'WARDING DEBT', wl_gravechoir: 'GRAVE CHOIR',
    wl_profane: 'PROFANE', wl_markedcollect: 'MARKED COLLECT', wl_deeptithe: 'DEEP TITHE', wl_lastcreditor: 'LAST CREDITOR',
    wl_blackprocession: 'BLACK PROCESSION', wl_debtreturns: 'THE DEBT RETURNS', cl_smallmercy: 'SMALL MERCY', cl_morningprayer: 'MORNING PRAYER',
    cl_brightword: 'BRIGHT WORD', cl_healingword: 'HEALING WORD', cl_blessing: 'BLESSING', cl_quietward: 'QUIET WARD',
    cl_sanctuary: 'SANCTUARY', cl_litany: 'LITANY', cl_consecrate: 'CONSECRATE', cl_shepherd: 'SHEPHERD',
    cl_daybreak: 'DAYBREAK', cl_highservice: 'HIGH SERVICE', cl_gooddoctor: 'GOOD DOCTOR', cl_vigil: 'VIGIL',
    cl_dawnkeeper: 'DAWN KEEPER', cl_smallgrace: 'SMALL GRACE', cl_oldshield: 'OLD SHIELD', cl_kindeye: 'KIND EYE',
    cl_intercession: 'INTERCESSION', cl_stillhands: 'STILL HANDS', cl_choirward: 'CHOIR WARD', cl_answered: 'ANSWERED',
    cl_guardiansaint: 'GUARDIAN SAINT', cl_warmlight: 'WARM LIGHT', cl_highlitany: 'HIGH LITANY', cl_secondservice: 'SECOND SERVICE',
    cl_hallowed: 'HALLOWED', cl_saintscall: 'SAINT\'S CALL', cl_brightvigil: 'BRIGHT VIGIL', cl_benediction: 'BENEDICTION',
    br_hotblood: 'HOT BLOOD', br_thickskin: 'THICK SKIN', br_warshout: 'WAR SHOUT', br_answerpain: 'ANSWER PAIN',
    br_recklesscut: 'RECKLESS CUT', br_ironhide: 'IRON HIDE', br_bloodfury: 'BLOOD FURY', br_cleave: 'CLEAVE',
    br_holdline: 'HOLD LINE', br_risingfury: 'RISING FURY', br_warstomp: 'WAR STOMP', br_recklessroar: 'RECKLESS ROAR',
    br_avalanche: 'AVALANCHE', br_longfury: 'LONG FURY', br_unbroken: 'UNBROKEN', br_answerback: 'ANSWER BACK',
    br_oldscars: 'OLD SCARS', br_bloodedge: 'BLOOD EDGE', br_battlehunger: 'BATTLE HUNGER', br_refusefall: 'REFUSE FALL',
    br_furywake: 'FURY WAKE', br_bloodlegend: 'BLOOD LEGEND', br_grudge: 'GRUDGE', br_warborn: 'WAR BORN',
    br_mountainstand: 'MOUNTAIN STAND', br_redmist: 'RED MIST', br_hardyears: 'HARD YEARS', br_lastmountain: 'LAST MOUNTAIN',
    br_bonebreaker: 'BONE BREAKER', br_neverdown: 'NEVER DOWN', fi_shieldup: 'SHIELD UP', fi_drill: 'DRILL',
    fi_pressin: 'PRESS IN', fi_shieldbash: 'SHIELD BASH', fi_guardpost: 'GUARD POST', fi_execute: 'EXECUTE',
    fi_phalanx: 'PHALANX', fi_riposte: 'RIPOSTE', fi_breakthrough: 'BREAKTHROUGH', fi_towershield: 'TOWER SHIELD',
    fi_holdfast: 'HOLDFAST', fi_veterancut: 'VETERAN CUT', fi_wallcaptain: 'WALL CAPTAIN', fi_champion: 'CHAMPION',
    fi_lastbastion: 'LAST BASTION', fi_braceup: 'BRACE UP', fi_oldguard: 'OLD GUARD', fi_steady: 'STEADY',
    fi_shieldwall2: 'SHIELD WALL II', fi_counterstep: 'COUNTER STEP', fi_drillmaster: 'DRILL MASTER', fi_ironline: 'IRON LINE',
    fi_secondwall: 'SECOND WALL', fi_readyroom: 'READY ROOM', fi_bulwarkborn: 'BULWARK BORN', fi_holdtheline: 'HOLD THE LINE',
    fi_grimparade: 'GRIM PARADE', fi_captainsword: 'CAPTAIN\'S SWORD', fi_greatwall: 'GREAT WALL', fi_neveryield: 'NEVER YIELD',
    ro_firstcut: 'FIRST CUT', ro_softstep: 'SOFT STEP', ro_nick: 'NICK', ro_venomedge: 'VENOM EDGE',
    ro_shadowstep: 'SHADOW STEP', ro_sharpen: 'SHARPEN', ro_twinfangs: 'TWIN FANGS', ro_combostart: 'COMBO START',
    ro_vanish: 'VANISH', ro_deepcut: 'DEEP CUT', ro_nightwork: 'NIGHT WORK', ro_masterstroke: 'MASTER STROKE',
    ro_redwork: 'RED WORK', ro_ghostblade: 'GHOST BLADE', ro_perfectheist: 'PERFECT HEIST', ro_alleyeyes: 'ALLEY EYES',
    ro_quickhands: 'QUICK HANDS', ro_grudgeknife: 'GRUDGE KNIFE', ro_slipfree: 'SLIP FREE', ro_backalley: 'BACK ALLEY',
    ro_eveningtrade: 'EVENING TRADE', ro_knifeweather: 'KNIFE WEATHER', ro_smokecall: 'SMOKE CALL', ro_paidwork: 'PAID WORK',
    ro_secondknife: 'SECOND KNIFE', ro_lastlaugh: 'LAST LAUGH', ro_fadeout: 'FADE OUT', ro_guildfavor: 'GUILD FAVOR',
    ro_midnightbook: 'MIDNIGHT BOOK', ro_deathanddebts: 'DEATH AND DEBTS', mo_openpalm: 'OPEN PALM', mo_stillwater: 'STILL WATER',
    mo_breathwork: 'BREATH WORK', mo_flurry: 'FLURRY', mo_slipcurrent: 'SLIP CURRENT', mo_redirect: 'REDIRECT',
    mo_riverform: 'RIVER FORM', mo_palmstrike: 'PALM STRIKE', mo_emptymind: 'EMPTY MIND', mo_fourwinds: 'FOUR WINDS',
    mo_ironpalm: 'IRON PALM', mo_watercourse: 'WATER COURSE', mo_stormhands: 'STORM HANDS', mo_mountainbreath: 'MOUNTAIN BREATH',
    mo_perfectform: 'PERFECT FORM', mo_quietfeet: 'QUIET FEET', mo_firstbreath: 'FIRST BREATH', mo_calmpool: 'CALM POOL',
    mo_counterflow: 'COUNTER FLOW', mo_slipstream: 'SLIP STREAM', mo_secondsun: 'SECOND SUN', mo_turningleaf: 'TURNING LEAF',
    mo_stillness: 'STILLNESS', mo_dawnpractice: 'DAWN PRACTICE', mo_thunderanswer: 'THUNDER ANSWER', mo_emptyroad: 'EMPTY ROAD',
    mo_longbreath: 'LONG BREATH', mo_stormcenter: 'STORM CENTER', mo_highform: 'HIGH FORM', mo_riversgift: 'RIVER\'S GIFT',
    wi_arcbolt: 'ARC BOLT', wi_wardglass: 'WARD GLASS', wi_sparkchip: 'SPARK CHIP', wi_frostgrip: 'FROST GRIP',
    wi_slowstop: 'SLOW STOP', wi_glassward: 'GLASS WARD', wi_stormlattice: 'STORM LATTICE', wi_mindpin: 'MIND PIN',
    wi_runeshield: 'RUNE SHIELD', wi_deepchannel: 'DEEP CHANNEL', wi_nullroom: 'NULL ROOM', wi_thirdcircle: 'THIRD CIRCLE',
    wi_greatlattice: 'GREAT LATTICE', wi_towerword: 'TOWER WORD', wi_archmage: 'ARCH MAGE', wi_glasscoat: 'GLASS COAT',
    wi_inkstill: 'INK STILL', wi_sparkback: 'SPARK BACK', wi_frostanswer: 'FROST ANSWER', wi_secondsight: 'SECOND SIGHT',
    wi_studyhall: 'STUDY HALL', wi_coldsnap: 'COLD SNAP', wi_mirrorcoat: 'MIRROR COAT', wi_morningtheory: 'MORNING THEORY',
    wi_stormanswer: 'STORM ANSWER', wi_towerstand: 'TOWER STAND', wi_thirdeye: 'THIRD EYE', wi_greatward: 'GREAT WARD',
    wi_wintercourt: 'WINTER COURT', wi_lastspell: 'LAST SPELL', so_smallsurge: 'SMALL SURGE', so_static: 'STATIC',
    so_sparkjump: 'SPARK JUMP', so_wildline: 'WILD LINE', so_jolt: 'JOLT', so_surgecall: 'SURGE CALL',
    so_stormblood: 'STORM BLOOD', so_wildstun: 'WILD STUN', so_glassstorm: 'GLASS STORM', so_chainarc: 'CHAIN ARC',
    so_overload: 'OVERLOAD', so_skyword: 'SKY WORD', so_tempesthand: 'TEMPEST HAND', so_wildcrown: 'WILD CROWN',
    so_stormheart: 'STORM HEART', so_staticskin: 'STATIC SKIN', so_bornspark: 'BORN SPARK', so_afterjolt: 'AFTER JOLT',
    so_risingstorm: 'RISING STORM', so_surgeback: 'SURGE BACK', so_stormnap: 'STORM NAP', so_wildanswer: 'WILD ANSWER',
    so_brightblood: 'BRIGHT BLOOD', so_morningarc: 'MORNING ARC', so_highvolt: 'HIGH VOLT', so_stormwall: 'STORM WALL',
    so_skyfire: 'SKY FIRE', so_tempestcrown: 'TEMPEST CROWN', so_eyewall: 'EYE WALL', so_stormborn: 'STORM BORN',
    pa_layhands: 'LAY HANDS', pa_oathmorning: 'OATH MORNING', pa_brightblow: 'BRIGHT BLOW', pa_smite: 'SMITE',
    pa_aegis: 'AEGIS', pa_warmaura: 'WARM AURA', pa_devotion: 'DEVOTION', pa_judgment: 'JUDGMENT',
    pa_shelter: 'SHELTER', pa_highoath: 'HIGH OATH', pa_dawnhammer: 'DAWN HAMMER', pa_standardup: 'STANDARD UP',
    pa_greatoath: 'GREAT OATH', pa_lightlance: 'LIGHT LANCE', pa_avatarlight: 'AVATAR LIGHT', pa_quietfaith: 'QUIET FAITH',
    pa_morninglight: 'MORNING LIGHT', pa_oldvow: 'OLD VOW', pa_lightanswer: 'LIGHT ANSWER', pa_griffonheart: 'GRIFFON HEART',
    pa_secondvow: 'SECOND VOW', pa_holdlight: 'HOLD LIGHT', pa_smitewake: 'SMITE WAKE', pa_sworndawn: 'SWORN DAWN',
    pa_castleheart: 'CASTLE HEART', pa_deepvow: 'DEEP VOW', pa_brightbanner: 'BRIGHT BANNER', pa_highshield: 'HIGH SHIELD',
    pa_lastcrusade: 'LAST CRUSADE', pa_graceperfect: 'PERFECT GRACE', ba_opennote: 'OPEN NOTE', ba_walkingtune: 'WALKING TUNE',
    ba_smallchorus: 'SMALL CHORUS', ba_brightverse: 'BRIGHT VERSE', ba_heartline: 'HEART LINE', ba_anthemcall: 'ANTHEM CALL',
    ba_marchbeat: 'MARCH BEAT', ba_crescendo2: 'CRESCENDO II', ba_sirenturn: 'SIREN TURN', ba_thirdverse: 'THIRD VERSE',
    ba_warballad: 'WAR BALLAD', ba_encorecall: 'ENCORE CALL', ba_greatchorus: 'GREAT CHORUS', ba_showstopper: 'SHOWSTOPPER',
    ba_maestrofinale: 'MAESTRO FINALE', ba_humalong: 'HUM ALONG', ba_roadsong: 'ROAD SONG', ba_bravetune: 'BRAVE TUNE',
    ba_secondwindsong: 'SECOND WIND SONG', ba_defiantnote: 'DEFIANT NOTE', ba_intermission: 'INTERMISSION', ba_rallyversc: 'RALLY VERSE',
    ba_steelchord: 'STEEL CHORD', ba_warmup2: 'WARM UP II', ba_heldnote: 'HELD NOTE', ba_lamentturn: 'LAMENT TURN',
    ba_fireballad: 'FIRE BALLAD', ba_arenaanthem: 'ARENA ANTHEM', ba_finalbow: 'FINAL BOW', ba_swansong: 'SWAN SONG',
    ra_markprey: 'MARK PREY', ra_scoutstep: 'SCOUT STEP', ra_steadyaim: 'STEADY AIM', ra_hawkeye: 'HAWK EYE',
    ra_wolfcall: 'WOLF CALL', ra_doubletrack: 'DOUBLE TRACK', ra_pintwice: 'PIN TWICE', ra_longshot: 'LONG SHOT',
    ra_packhunt: 'PACK HUNT', ra_killzone: 'KILL ZONE', ra_denmother: 'DEN MOTHER', ra_deadeye: 'DEADEYE',
    ra_hunterstorm: 'HUNTER STORM', ra_wildpack: 'WILD PACK', ra_apexhunt: 'APEX HUNT', ra_trailsense: 'TRAIL SENSE',
    ra_morningscout: 'MORNING SCOUT', ra_thornpath: 'THORN PATH', ra_foxsense: 'FOX SENSE', ra_owleye: 'OWL EYE',
    ra_campcraft: 'CAMP CRAFT', ra_bearfriend: 'BEAR FRIEND', ra_pinrain: 'PIN RAIN', ra_earlywatch: 'EARLY WATCH',
    ra_falcondive: 'FALCON DIVE', ra_wolfwinter: 'WOLF WINTER', ra_silentwood: 'SILENT WOOD', ra_packleader: 'PACK LEADER',
    ra_longwinter: 'LONG WINTER', ra_lastarrow: 'LAST ARROW', dr_barkkin: 'BARK KIN', dr_seedling: 'SEEDLING',
    dr_sapflow: 'SAP FLOW', dr_thornscoat: 'THORN COAT', dr_wildswipe: 'WILD SWIPE', dr_greenmend: 'GREEN MEND',
    dr_verdant: 'VERDANT', dr_bearform: 'BEAR FORM', dr_grovecall: 'GROVE CALL', dr_deeproot: 'DEEP ROOT',
    dr_wildhunt: 'WILD HUNT', dr_springrite: 'SPRING RITE', dr_oldforest: 'OLD FOREST', dr_stagking: 'STAG KING',
    dr_worldtree: 'WORLD TREE', dr_mossskin: 'MOSS SKIN', dr_morningdew: 'MORNING DEW', dr_greenpulse: 'GREEN PULSE',
    dr_thornanswer: 'THORN ANSWER', dr_wildblood: 'WILD BLOOD', dr_nightgrowth: 'NIGHT GROWTH', dr_rootwall: 'ROOT WALL',
    dr_regrowth2: 'REGROWTH II', dr_earlyspring: 'EARLY SPRING', dr_greatoak: 'GREAT OAK', dr_wildwake: 'WILD WAKE',
    dr_mistveil: 'MIST VEIL', dr_forestheart: 'FOREST HEART', dr_stampede: 'STAMPEDE', dr_gaiasword: 'GAIA\'S SWORD',
    ar_quickrig: 'QUICK RIG', ar_toolbelt: 'TOOL BELT', ar_tinker: 'TINKER', ar_overcharge: 'OVERCHARGE',
    ar_turretpop: 'TURRET POP', ar_springtrap: 'SPRING TRAP', ar_fielddrone: 'FIELD DRONE', ar_shockcoil: 'SHOCK COIL',
    ar_bunkerkit: 'BUNKER KIT', ar_twinturret: 'TWIN TURRET', ar_railshot: 'RAIL SHOT', ar_powercell: 'POWER CELL',
    ar_warfactory: 'WAR FACTORY', ar_siegework: 'SIEGE WORK', ar_masterwork: 'MASTERWORK', ar_sparkplug: 'SPARK PLUG',
    ar_workshopdust: 'WORKSHOP DUST', ar_failsafe: 'FAIL SAFE', ar_autoloader: 'AUTO LOADER', ar_bracefield: 'BRACE FIELD',
    ar_nightshift: 'NIGHT SHIFT', ar_emergencyrig: 'EMERGENCY RIG', ar_backupbot: 'BACKUP BOT', ar_primedcoil: 'PRIMED COIL',
    ar_ironworks: 'IRONWORKS', ar_redline: 'RED LINE', ar_patentwall: 'PATENT WALL', ar_fortress: 'FORTRESS',
    ar_deadhand: 'DEAD HAND', ar_magnumopus: 'MAGNUM OPUS', u_steadyhand: 'STEADY HAND', u_leatherguard: 'LEATHER GUARD',
    u_charmward: 'CHARM WARD', u_smallstep: 'SMALL STEP', u_firstpress: 'FIRST PRESS', u_smallsong: 'SMALL SONG',
    u_roadpin: 'ROAD PIN', u_fieldwrap: 'FIELD WRAP', u_bracetwo: 'BRACE TWO', u_quickstudy: 'QUICK STUDY',
    u_secondknife: 'SPARE KNIFE', u_holdcharm: 'HOLD CHARM', u_warcadence: 'WAR CADENCE', u_pressline: 'PRESS LINE',
    u_ironpatch: 'IRON PATCH', u_luckcoin: 'LUCK COIN', u_oldbanner: 'OLD BANNER', u_secondline: 'SECOND LINE',
    u_fadecloak: 'FADE CLOAK', u_warhorn: 'WAR HORN', u_stonebones: 'STONE BONES', u_grandgesture: 'GRAND GESTURE',
    u_secondbreath: 'SECOND BREATH', u_clearsight: 'CLEAR SIGHT', u_firmfooting: 'FIRM FOOTING', u_smallward: 'SMALL WARD',
    u_walkoff: 'WALK OFF', u_watchful: 'WATCHFUL', u_bracetogether: 'BRACE TOGETHER', u_fielddressing: 'FIELD DRESSING',
    u_smallopening: 'SMALL OPENING', u_graystone: 'GRAY STONE', u_followthrough: 'FOLLOW THROUGH', u_pressurepoint: 'PRESSURE POINT',
    u_resilientline: 'RESILIENT LINE', u_gatheringnerve: 'GATHERING NERVE', u_sharedrecovery: 'SHARED RECOVERY', u_quietanger: 'QUIET ANGER',
    u_turnthetide: 'TURN THE TIDE', u_layereddefense: 'LAYERED DEFENSE', u_holdfast: 'HOLD FAST', u_smallmiracle: 'SMALL MIRACLE',
    u_lastreserve: 'LAST RESERVE', u_perfectmoment: 'PERFECT MOMENT', u_oldoath: 'OLD OATH', u_finalword: 'FINAL WORD',
    u_daybreaker: 'DAYBREAKER', u_nightlantern: 'NIGHT LANTERN', u_carriedcharm: 'CARRIED CHARM', u_travelerstale: 'TRAVELER\'S TALE',
    /* Engine 63/64 append-only ability waves. Curated here so compound stable
       ids never leak onto a player-facing card as one glued word. */
    ra_beastcall_v2: 'BEAST CALL', ar_backupbot_v2: 'BACKUP GUARD', dr_briarchild: 'BRIAR CHILD',
    u_lastremedy: 'LAST REMEDY', ti_emberremains: 'EMBER REMAINS', wl_lastgate: 'LAST GATE',
    u_highwatch: 'HIGH WATCH', u_everguard: 'EVERGUARD', u_firstlight: 'FIRST LIGHT',
    u_steadfastanswer: 'STEADFAST ANSWER', u_ironconstellation: 'IRON CONSTELLATION',
    u_endlesscadence: 'ENDLESS CADENCE', u_lastward: 'LAST WARD',
    hu_rallyingblow: 'RALLYING BLOW', hu_unbrokenline: 'UNBROKEN LINE',
    el_starstep: 'STAR STEP', el_moonward: 'MOON WARD',
    he_twofoldverse: 'TWOFOLD VERSE', he_sharedgrace: 'SHARED GRACE',
    dw_hammeroath: 'HAMMER OATH', dw_stonevow: 'STONE VOW',
    hf_luckystep: 'LUCKY STEP', hf_braveheart: 'BRAVE HEART',
    gn_clockworktrick: 'CLOCKWORK TRICK', gn_hiddenfailsafe: 'HIDDEN FAILSAFE',
    db_dragonroar: 'DRAGON ROAR', db_ancestralguard: 'ANCESTRAL GUARD',
    ti_hellfirebargain: 'HELLFIRE BARGAIN',
    ho_relentlessswing: 'RELENTLESS SWING', ho_holdtheline: 'HOLD THE LINE',
    or_savageanswer: 'SAVAGE ANSWER', or_ironblood: 'IRON BLOOD'
  };
  /* ============ R457 · WHO IS STANDING THERE ============
     Jan: "for all the events in the middle like spirit draw or even the
     shops, show a random character from the hall as the host just like how
     explore the hall events all have a host character ... and make sure the
     characters are relevant as well in terms of class and race for the
     specific event."

     This table is the RELEVANCE half of that ask, and it lives here rather
     than in the engine for the usual reason: the engine's bytes are spliced
     into the worker, and who a screen casts as the shopkeeper cannot be
     allowed to force a worker paste. It is display only, top to bottom.

     Each row is preferences, never a requirement. The picker in the battle
     UI walks class first, then race, then anybody with a portrait, so a Hall
     with no Warlocks in it still gets a face on every sheet. The keys are the
     engine's own event ids (EVENTS in hoh-contract-engine.js) plus 'shop'.
     ARCH_HOSTS underneath is the safety net for any event id this table has
     not learned yet, so a new event can never render hostless.

     One deliberate restraint. The host is somebody's real character, so the
     sheet NAMES them and shows their race and class, and never claims they
     ARE the thing in the title. Nobody's paladin gets labelled the devil. */
  var EVENT_HOSTS = {
    /* gift */
    ev_moonwell:  { cls: ['Druid', 'Cleric', 'Ranger'],            race: ['Elf', 'Half-Elf', 'Fairy'] },
    ev_banner:    { cls: ['Fighter', 'Paladin', 'Barbarian'],      race: ['Human', 'Dragonborn', 'Goliath'] },
    ev_shrine:    { cls: ['Cleric', 'Paladin'],                    race: ['Aasimar', 'Human', 'Dwarf'] },
    ev_quiet:     { cls: ['Druid', 'Ranger', 'Monk'],              race: ['Halfling', 'Gnome', 'Elf'] },
    ev_echo:      { cls: ['Bard', 'Wizard'],                       race: ['Half-Elf', 'Human', 'Elf'] },
    ev_cache:     { cls: ['Artificer', 'Rogue', 'Bard'],           race: ['Gnome', 'Halfling', 'Tabaxi'] },
    /* gamble */
    ev_chest:     { cls: ['Rogue', 'Artificer'],                   race: ['Halfling', 'Goblin', 'Kobold'] },
    ev_coin:      { cls: ['Rogue', 'Bard'],                        race: ['Halfling', 'Tiefling', 'Tabaxi'] },
    ev_reliquary: { cls: ['Warlock', 'Cleric'],                    race: ['Tiefling', 'Aasimar', 'Drow'] },
    ev_fable:     { cls: ['Bard', 'Rogue'],                        race: ['Halfling', 'Gnome', 'Tabaxi'] },
    ev_idol:      { cls: ['Warlock', 'Cleric', 'Monk'],            race: ['Tiefling', 'Drow', 'Aasimar'] },
    /* toll */
    ev_bridge:    { cls: ['Fighter', 'Barbarian'],                 race: ['Goliath', 'Half-Orc', 'Orc', 'Dwarf'] },
    ev_bloodgate: { cls: ['Warlock', 'Paladin'],                   race: ['Tiefling', 'Drow', 'Orc'] },
    ev_tribute:   { cls: ['Cleric', 'Paladin', 'Monk'],            race: ['Aasimar', 'Human', 'Dwarf'] },
    ev_wardgate:  { cls: ['Wizard', 'Artificer', 'Sorcerer'],      race: ['Gnome', 'Elf', 'Warforged'] },
    /* deal */
    ev_devils:    { cls: ['Warlock', 'Sorcerer'],                  race: ['Tiefling', 'Drow', 'Changeling'] },
    ev_glutton:   { cls: ['Barbarian', 'Fighter', 'Druid'],        race: ['Halfling', 'Dwarf', 'Goliath'] },
    ev_glass:     { cls: ['Rogue', 'Sorcerer'],                    race: ['Elf', 'Drow', 'Changeling'] },
    ev_oathstone: { cls: ['Paladin', 'Cleric'],                    race: ['Dwarf', 'Warforged', 'Human'] },
    ev_giant:     { cls: ['Barbarian', 'Druid'],                   race: ['Goliath', 'Orc', 'Half-Orc'] },
    ev_gravetoll: { cls: ['Warlock', 'Cleric'],                    race: ['Tiefling', 'Drow', 'Aasimar'] },
    /* gated */
    ev_alcove:    { cls: ['Cleric', 'Paladin'],                    race: ['Aasimar', 'Human', 'Dwarf'] },
    ev_lockbox:   { cls: ['Rogue', 'Artificer'],                   race: ['Halfling', 'Gnome', 'Goblin'] },
    ev_seal:      { cls: ['Warlock', 'Wizard', 'Sorcerer'],        race: ['Tiefling', 'Drow', 'Elf'] },
    ev_trail:     { cls: ['Ranger', 'Druid'],                      race: ['Elf', 'Half-Elf', 'Tabaxi', 'Harengon'] },
    /* chains */
    ch_squire:    { cls: ['Fighter', 'Paladin'],                   race: ['Human', 'Half-Elf', 'Halfling'] },
    ch_blade:     { cls: ['Fighter', 'Warlock'],                   race: ['Human', 'Drow', 'Tiefling'] },
    ch_shadow:    { cls: ['Rogue', 'Ranger'],                      race: ['Drow', 'Changeling', 'Tabaxi'] },
    ch_ember:     { cls: ['Cleric', 'Sorcerer'],                   race: ['Aasimar', 'Genasi', 'Tiefling'] },
    /* companions: the host is the body being offered */
    co_merc:      { cls: ['Fighter'],                              race: ['Human', 'Half-Orc', 'Orc', 'Goliath'] },
    co_hound:     { cls: ['Ranger', 'Druid'],                      race: ['Tabaxi', 'Harengon', 'Human'] },
    co_caged:     { cls: ['Cleric'],                               race: ['Aasimar', 'Human', 'Elf'] },
    co_fallen:    { cls: ['Paladin'],                              race: ['Human', 'Aasimar', 'Dragonborn'] },
    /* shrines */
    sh_ash:       { cls: ['Wizard', 'Sorcerer'],                   race: ['Genasi', 'Tiefling', 'Dragonborn'] },
    sh_blood:     { cls: ['Barbarian', 'Warlock'],                 race: ['Orc', 'Half-Orc', 'Tiefling'] },
    sh_warden:    { cls: ['Cleric', 'Paladin', 'Artificer'],       race: ['Dwarf', 'Warforged', 'Goliath'] },
    sh_choir:     { cls: ['Bard', 'Cleric'],                       race: ['Aasimar', 'Elf', 'Half-Elf'] },
    /* reactive */
    re_mercy:     { cls: ['Druid', 'Cleric', 'Ranger'],            race: ['Halfling', 'Human', 'Gnome'] },
    re_thief:     { cls: ['Rogue'],                                race: ['Goblin', 'Halfling', 'Kobold', 'Tabaxi'] },
    re_arcane:    { cls: ['Wizard', 'Sorcerer'],                   race: ['Elf', 'Genasi', 'Gnome'] },
    re_test:      { cls: ['Fighter', 'Barbarian'],                 race: ['Goliath', 'Orc', 'Dragonborn', 'Half-Orc'] },
    /* campfires */
    cf_longrest:  { cls: ['Bard', 'Druid', 'Ranger'],              race: ['Halfling', 'Human', 'Gnome'] },
    cf_smithy:    { cls: ['Artificer', 'Fighter'],                 race: ['Dwarf', 'Warforged', 'Gnome'] },
    cf_watch:     { cls: ['Ranger', 'Fighter'],                    race: ['Human', 'Elf', 'Half-Elf'] },
    cf_story:     { cls: ['Bard'],                                 race: ['Halfling', 'Gnome', 'Human', 'Elf'] },
    /* minigames */
    mg_chests:    { cls: ['Rogue', 'Artificer'],                   race: ['Halfling', 'Goblin', 'Gnome'] },
    mg_rune:      { cls: ['Wizard', 'Artificer'],                  race: ['Gnome', 'Dwarf', 'Elf'] },
    mg_draw:      { cls: ['Warlock', 'Sorcerer', 'Bard'],          race: ['Tiefling', 'Drow', 'Changeling'] },
    mg_silhouette:{ cls: ['Bard', 'Rogue'],                        race: ['Changeling', 'Half-Elf', 'Human'] },
    mg_doors:     { cls: ['Wizard', 'Monk'],                       race: ['Elf', 'Warforged', 'Gnome'] },
    mg_riddle:    { cls: ['Sorcerer', 'Warlock', 'Bard'],          race: ['Gnome', 'Fairy', 'Tiefling'] },
    mg_pairs:     { cls: ['Bard', 'Artificer'],                    race: ['Halfling', 'Gnome', 'Human'] },
    mg_forecast:  { cls: ['Ranger', 'Druid', 'Wizard'],            race: ['Elf', 'Tabaxi', 'Half-Elf'] },
    mg_scale:     { cls: ['Cleric', 'Monk', 'Artificer'],          race: ['Aasimar', 'Dwarf', 'Warforged'] },
    mg_path:      { cls: ['Ranger', 'Rogue', 'Druid'],             race: ['Elf', 'Tabaxi', 'Drow'] },
    /* the shop stop is not an engine event, so it carries its own key */
    shop:         { cls: ['Artificer', 'Bard', 'Rogue'],           race: ['Gnome', 'Halfling', 'Dwarf', 'Tabaxi'] }
  };
  /* the net under the table: a brand new event id still gets a face that
     belongs to its archetype rather than a random stranger */
  var ARCH_HOSTS = {
    gift:      { cls: ['Cleric', 'Druid', 'Bard'],        race: [] },
    gamble:    { cls: ['Rogue', 'Bard'],                  race: ['Halfling', 'Tabaxi'] },
    toll:      { cls: ['Fighter', 'Barbarian'],           race: ['Goliath', 'Dwarf'] },
    deal:      { cls: ['Warlock', 'Sorcerer'],            race: ['Tiefling', 'Drow'] },
    gated:     { cls: ['Wizard', 'Rogue', 'Cleric'],      race: [] },
    chain:     { cls: ['Fighter', 'Rogue'],               race: ['Human'] },
    companion: { cls: ['Fighter', 'Ranger', 'Cleric'],    race: [] },
    shrine:    { cls: ['Cleric', 'Paladin', 'Warlock'],   race: ['Aasimar', 'Dwarf'] },
    reactive:  { cls: ['Ranger', 'Fighter'],              race: [] },
    campfire:  { cls: ['Bard', 'Druid'],                  race: ['Halfling', 'Human'] },
    minigame:  { cls: ['Bard', 'Artificer', 'Wizard'],    race: ['Gnome'] }
  };
  /* ============ R458 · WHAT KIND OF PARTY IS THAT ============
     Jan: "increase the amount of variety in how enemies spawn and appear,
     like the different types of parties you can come across and different
     effects they have."

     THE VARIETY WAS ALREADY THERE AND INVISIBLE, which is the whole finding.
     The engine has eight party shapes plus the boss and the rival
     (LADDER_SHAPES in hoh-contract-engine.js), and they are genuinely
     different fights: a duel fields two bodies at 1.75x the stat lock with
     two on the bench, a horde fields six or more at 0.38x with none. The
     screen showed the same three words for all of them ("A Motley Table") or
     a race and class title that says nothing about the shape. A player
     cannot experience variety they cannot perceive.

     So every shape gets ONE plain line that states what it means for the
     person playing, and a colour. No new fantasy names to learn: the fiction
     title (THE CIRCLE ARCANE, THE BLOODHORN WARBAND) still sits above it and
     does the flavour, and this line does the information. Copy-voice: chrome
     is never fiction, and "line", "band" and "table" stay out of it as names
     for a team.

     R458, CAUGHT IN REVIEW · TWO BUGS IN THIS TABLE, BOTH LOAD-BEARING.

     1. THE COUNTS WERE FICTION. Every line hard-coded an absolute number
        ("Two of them", "Four across"), but the tiles on screen are
        E.ladderBodies(stage, shape) = max(shape.min, round(ladderCore(stage)
        * shape.n)), and ladderCore climbs from 2 to 10 with the stage. The
        sentence is only true while the `min` floor still binds, roughly to
        stage 18. Measured against the real engine: 30 of the 48 fight stages
        in 1-60 disagree, INCLUDING stage 31, where Jan is parked and which
        he checks first — it said "Two of them" over three bodies. Stage 59
        said "Five and a bench" over ten. The one row R458 added so the shape
        could be COUNTED is the one row a player can count and find wrong.
        So the count is gone from the copy and composed at the board from
        foeBoardSize(), the same call that builds the tiles. `tail` is the
        half that was always true; boss and rival keep a whole `say` because
        they state a fact rather than a count.

     2. THREE COLOURS WERE ALREADY SPOKEN FOR. `hi` is written onto --sc of
        .hoc-side.foe, which drives the enemy name, the radial tint, EVERY
        portrait rim, the acting ring and the win glow. elite was #e8c674 —
        the player's own gold, and the rule three lines above the ring in the
        stylesheet says why that is not a taste question: "R300: each side
        glows its OWN colour when acting — the enemy borrowed your gold
        before, which read as your hero taking the turn." R458 reinstated a
        bug R300 had already paid to fix, on one stage in ten. warband was
        #7ec4cf, the teal the stylesheet explicitly RESERVES for a wanderer
        because it must belong to neither side. horde was in the ally/heal
        green family. All three now sit in the enemy violet band, which also
        keeps them honest against the nine places that still hardcode
        #b46bff as "the enemy" (the log tag, the die, the coin, the flying
        numbers), so the screen carries one enemy identity and not two.
        duel and pair moved off the warm orange lane as well: they were
        within a few points of ACT_COLORS.shield #e08a5a, so a shield pop on
        those stages vanished into the side tint.

     The standing constraint for any future hue: never the player's gold
     (#c9a84c / #e8c674), never the ally or heal green (#8fe0a5 / #7ed49a),
     never the reserved wanderer teal (#7ec4cf), and far enough from
     ACT_COLORS.shield #e08a5a to read against it. */
  var FOE_SHAPES = {
    duel:    { tail: 'and they swing hard',              hi: '#d0568c' },
    pair:    { tail: 'hitting above their weight',       hi: '#c264b8' },
    trio:    { tail: 'an even fight',                    hi: '#b46bff' },
    squad:   { tail: 'each one softer',                  hi: '#9a78e0' },
    line:    { tail: 'thin and quick to break',          hi: '#8f8ae8' },
    horde:   { tail: 'and every one light',              hi: '#6b4fae' },
    elite:   { tail: 'with a deep bench behind',         hi: '#d9b3ff' },
    warband: { tail: 'all soft, with a bench behind', hi: '#a04fd0' },
    mini:    { say: 'A mini-boss with its own visible rule', hi: '#b46bff' },
    boss:    { say: 'Their bench takes bonus actions and protects the boss', hi: '#ef6b6b' },
    rival:   { say: 'Another hall, with their real party and gear', hi: '#ef94ff' }
  };
  function foeShape(id) { return FOE_SHAPES[String(id || '')] || null; }

  /* ============ R458 · THE BOSS SPEAKS (finally) ============
     THIS COPY IS NOT NEW. It was authored in engine v46 as LADDER_BOSS_VOICE,
     six voices of four lines each, and the engine even stamps `poss: 1` and
     `vo: <index>` onto the boss row with a comment saying that choosing which
     portrait is possessed "is the UI's job, and it draws a FramedFantasy face
     for this seat."
     The UI never did either. LADDER_BOSS_VOICE is not in the engine's export
     list, nothing reads `poss` or `vo`, and twenty-four authored lines have
     never once reached a player. So R458 does the UI's half of a v46 design:
     the decade boss wears a real commission (Jan's ruling, and the engine's
     own stated intent), and it says what it is.

     THE COPY LIVES HERE, not there, for the R453 reason: the engine's bytes
     are spliced into the worker, so a copy fix would force a worker paste. The
     engine's table stays where it is as the record of the intent.
     One line was edited on the way across: "one of your line" became "one of
     your party", because `line` is a banned costume word for a team. */
  var BOSS_VOICES = [
    { open: 'Something wearing this hero speaks with their mouth.',
      call: 'It calls, and the dark answers in their voice.',
      hex:  'It looks at you and you forget how to move.',
      fall: 'The hero drops to one knee. Whatever held them lets go.' },
    { open: 'They were one of yours this morning. They are not, now.',
      call: 'Others rise behind them, wearing the same grin.',
      hex:  'A cold hand closes around one of your party.',
      fall: 'The grin fades. Only the hero is left, and they are breathing.' },
    { open: 'Their eyes have gone out. Something else is home.',
      call: 'It whistles. Shapes come running.',
      hex:  'It whispers a name and that hero stops dead.',
      fall: 'The light comes back on behind their eyes.' },
    { open: 'A friend of the Hall stands in your way and does not know it.',
      call: 'It will not fight you alone. It never intended to.',
      hex:  'One of yours is held where they stand.',
      fall: 'They fall, and for the first time they look like themselves.' },
    { open: 'The portrait moves. The person inside it is screaming.',
      call: 'It reaches back and pulls more of them through.',
      hex:  'It takes a turn that was not its own to take.',
      fall: 'Quiet. Then a cough, and a voice you recognise.' },
    { open: 'This one was painted for the Hall. Something repainted it.',
      call: 'Its court arrives without being asked twice.',
      hex:  'A hero of yours stares at nothing and loses the beat.',
      fall: 'The paint settles. The hero is still under it.' }
  ];
  function bossVoice(n) { return BOSS_VOICES[((n | 0) % BOSS_VOICES.length + BOSS_VOICES.length) % BOSS_VOICES.length]; }

  function hostPrefs(id, arch) {
    var row = EVENT_HOSTS[String(id || '')] || ARCH_HOSTS[String(arch || '')] || null;
    return { cls: (row && row.cls) || [], race: (row && row.race) || [] };
  }

  function _eng() {
    try {
      var g = (typeof self !== 'undefined') ? self : (typeof global !== 'undefined' ? global : null);
      return (g && g.HOHContract) || null;
    } catch (e) { return null; }
  }
  function abilDesc(id) {
    var E = _eng();
    var a = (E && E.abDef) ? E.abDef(id) : null;
    return (a && a.t) ? String(a.t) : '';
  }
  /* ⛔ R638: THE SENTENCE CAME THROUGH AND THE NAME DID NOT, FOR ALL 669.
     abilDesc above has reached the engine since the compiled rows landed, so
     a card's ability text has always read correctly. Its name never had this
     twin, so skillName fell past SKILLS and ABIL_NAMES (neither of which
     holds a single ab_ key) into prettyId, which upcases the id itself.
     Measured on the shipped files: skillName('ab_73ecfe5e_r3') returned
     "73ECFE5E R3" beside the correct sentence "When this hero dies, revive
     this hero and gain armor 2." Jan's word for it, "Not Yet", is in the
     engine row and reached exactly one surface in the whole game, the battle
     log's fire line. The card, the armory chip, the party tray, the roll reel
     and the roll announcement all printed the hash.
     One door, because the compiler already puts the name in one place. */
  function abilName(id) {
    var E = _eng();
    var a = (E && E.abDef) ? E.abDef(id) : null;
    return (a && a.name) ? String(a.name) : '';
  }
  function prettyId(id) {
    return String(id).replace(/^[a-z]{1,3}_/, '').replace(/_/g, ' ').toUpperCase();
  }

  return {
    RARITY_NAMES: RARITY_NAMES, RARITY_COLORS: RARITY_COLORS, STAT_COLORS: STAT_COLORS,
    SKILLS: SKILLS, PASSIVES: PASSIVES, CONDS: CONDS, MOVE_TIPS: MOVE_TIPS,
    /* R457: who hosts which encounter. Display only, see the table's header. */
    EVENT_HOSTS: EVENT_HOSTS, ARCH_HOSTS: ARCH_HOSTS, hostPrefs: hostPrefs,
    /* R458: the party shape, said in plain words. Display only. */
    FOE_SHAPES: FOE_SHAPES, foeShape: foeShape,
    BOSS_VOICES: BOSS_VOICES, bossVoice: bossVoice,
    CALLINGS: CALLINGS, ABIL_NAMES: ABIL_NAMES,
    /* R453: the plain-words door. Every engine-authored description goes
       through this on its way to a player. Names never do. */
    plain: plain,
    callingName: function (id) { return (CALLINGS[id] || { n: 'RECRUIT' }).n; },
    callingVerb: function (id) { return (CALLINGS[id] || { v: 'takes their turn' }).v; },
    callingDesc: function (id) { return (CALLINGS[id] || { d: '' }).d; },
    lineFor: lineFor, NOTE_TEXT: NOTE_TEXT,
    /* R638: the engine row sits between the hand-written tables and the
       hash, exactly where abilDesc already sits for the sentence. A legacy
       id still wins from SKILLS/ABIL_NAMES, so nothing a player holds today
       can change name. */
    skillName: function (id) { return (SKILLS[id] || {}).n || ABIL_NAMES[id] || abilName(id) || prettyId(id); },
    skillDesc: function (id) { return plain((SKILLS[id] || {}).d || abilDesc(id)); },
    passiveName: function (id) { return (PASSIVES[id] || {}).n || ABIL_NAMES[id] || abilName(id) || prettyId(id); },
    passiveDesc: function (id) { return plain((PASSIVES[id] || {}).d || abilDesc(id)); }
  };
}));
